import { useState, useEffect } from 'react';
import { useGameState } from '../../net/GameProvider.js';
import { useI18n } from '../../i18n.js';
import { calculateLevel } from '@big2/shared';
import { PlayerProfileModal } from './PlayerProfileModal.js';

interface FriendsSectionProps {
  onJoinRoom?: (roomId: string) => void;
  onOpenAuth?: (view?: 'signin' | 'signup') => void;
}

export function FriendsSection({ onJoinRoom, onOpenAuth }: FriendsSectionProps) {
  const { state, actions } = useGameState();
  const { lang, t } = useI18n();
  const isMn = lang === 'mn';

  const [targetInput, setTargetInput] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState<{ message: string; isError: boolean } | null>(null);
  const [inspectedFriend, setInspectedFriend] = useState<{ userId: string; name: string } | null>(null);

  // Automatically fetch friends on mount if signed in
  useEffect(() => {
    if (state.profile) {
      actions.getFriends();
    }
  }, [state.profile?.id]);

  if (!state.profile) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 20px' }}>
        <div style={{ fontSize: '3rem', marginBottom: 16 }}>👥</div>
        <h3 style={{ margin: '0 0 8px 0', color: '#fff', fontSize: '1.25rem' }}>
          {isMn ? 'Найзуудтайгаа хамт тоглоорой' : 'Play With Friends'}
        </h3>
        <p style={{ margin: '0 0 20px 0', color: 'rgba(255,255,255,0.7)', fontSize: '0.9rem', maxWidth: 360, marginLeft: 'auto', marginRight: 'auto' }}>
          {isMn
            ? 'Бүртгүүлж нэвтэрснээр найзуудаа нэмэх, онлайн байгаа эсэхийг нь харах, нэг товшилтоор өрөөнд нь нэгдэх боломжтой.'
            : 'Sign in or register to add friends, see their online status, and join their games with a single click.'}
        </p>
        <button
          type="button"
          className="profile-primary-button"
          onClick={() => onOpenAuth?.('signup')}
          style={{ width: 'auto', padding: '10px 24px' }}
        >
          {t.signUpAction}
        </button>
      </div>
    );
  }

  const friendsData = state.friendsData;
  const friends = friendsData?.friends ?? [];
  const incoming = friendsData?.incomingRequests ?? [];
  const outgoing = friendsData?.outgoingRequests ?? [];

  const handleSendRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    const target = targetInput.trim();
    if (!target) return;

    setSubmitting(true);
    setFeedback(null);
    try {
      actions.sendFriendRequest({ displayNameOrEmail: target });
      setTargetInput('');
      setFeedback({
        message: t.friendRequestSent,
        isError: false,
      });
      setTimeout(() => setFeedback(null), 4000);
    } catch (err: any) {
      setFeedback({
        message: err?.message || 'Error sending request',
        isError: true,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveFriend = (friendUserId: string, friendName: string) => {
    const confirmMsg = isMn
      ? `${friendName}-г найзын жагсаалтаас хасах уу?`
      : `Remove ${friendName} from your friends list?`;
    if (window.confirm(confirmMsg)) {
      actions.removeFriend(friendUserId);
    }
  };

  return (
    <div className="friends-section" style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* Search & Add Friend Box */}
      <div
        style={{
          background: 'rgba(255, 255, 255, 0.04)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: 12,
          padding: 16,
        }}
      >
        <h4 style={{ margin: '0 0 12px 0', fontSize: '0.95rem', color: '#fde047', display: 'flex', alignItems: 'center', gap: 8 }}>
          <span>➕</span> {t.addFriend}
        </h4>
        <form onSubmit={handleSendRequest} style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <input
            type="text"
            className="profile-input"
            placeholder={t.addFriendPlaceholder}
            value={targetInput}
            onChange={(e) => setTargetInput(e.target.value)}
            disabled={submitting}
            style={{ flex: '1 1 200px' }}
          />
          <button
            type="submit"
            className="profile-primary-button"
            disabled={submitting || !targetInput.trim()}
            style={{ width: 'auto', padding: '0 20px', whiteSpace: 'nowrap' }}
          >
            {submitting ? '...' : t.addFriend}
          </button>
        </form>
        {feedback && (
          <div
            style={{
              marginTop: 10,
              fontSize: '0.85rem',
              color: feedback.isError ? '#f87171' : '#4ade80',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <span>{feedback.isError ? '⚠️' : '✓'}</span> {feedback.message}
          </div>
        )}
      </div>

      {/* Incoming Requests */}
      {incoming.length > 0 && (
        <div
          style={{
            background: 'rgba(234, 179, 8, 0.08)',
            border: '1px solid rgba(234, 179, 8, 0.25)',
            borderRadius: 12,
            padding: 16,
          }}
        >
          <h4 style={{ margin: '0 0 12px 0', fontSize: '0.95rem', color: '#fde047', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span>📬</span> {t.incomingFriendRequests} ({incoming.length})
          </h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {incoming.map((req) => (
              <div
                key={req.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                  background: 'rgba(0, 0, 0, 0.25)',
                  padding: '10px 14px',
                  borderRadius: 8,
                  flexWrap: 'wrap',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #6366f1, #a855f7)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 700,
                      color: '#fff',
                      fontSize: '0.9rem',
                    }}
                  >
                    {req.fromDisplayName.slice(0, 1).toUpperCase()}
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, color: '#fff', fontSize: '0.95rem' }}>
                      {req.fromDisplayName}
                    </div>
                    <div style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.6)' }}>
                      {t.levelLabel(req.fromLevel)}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    type="button"
                    onClick={() => actions.respondFriendRequest(req.id, true)}
                    style={{
                      background: '#16a34a',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 6,
                      padding: '6px 12px',
                      fontSize: '0.85rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    ✓ {t.acceptFriend}
                  </button>
                  <button
                    type="button"
                    onClick={() => actions.respondFriendRequest(req.id, false)}
                    style={{
                      background: 'rgba(255, 255, 255, 0.1)',
                      color: '#f87171',
                      border: '1px solid rgba(248, 113, 113, 0.3)',
                      borderRadius: 6,
                      padding: '6px 12px',
                      fontSize: '0.85rem',
                      cursor: 'pointer',
                    }}
                  >
                    ✕ {t.rejectFriend}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Friends List */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <h4 style={{ margin: 0, fontSize: '1rem', color: '#fff', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span>👥</span> {t.friendsList} ({friends.length})
          </h4>
          <button
            type="button"
            onClick={() => actions.getFriends()}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'rgba(255,255,255,0.6)',
              fontSize: '0.85rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 4,
            }}
            title={isMn ? 'Шинэчлэх' : 'Refresh'}
          >
            🔄
          </button>
        </div>

        {friends.length === 0 ? (
          <div
            style={{
              padding: '32px 16px',
              textAlign: 'center',
              color: 'rgba(255, 255, 255, 0.5)',
              background: 'rgba(255, 255, 255, 0.02)',
              border: '1px dashed rgba(255, 255, 255, 0.1)',
              borderRadius: 12,
              fontSize: '0.9rem',
            }}
          >
            {t.noFriendsYet}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {friends.map((friend) => {
              const levelInfo = calculateLevel(friend.xp);
              return (
                <div
                  key={friend.userId}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 12,
                    background: 'rgba(255, 255, 255, 0.04)',
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                    padding: '12px 16px',
                    borderRadius: 10,
                    flexWrap: 'wrap',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    {/* Avatar & Online status dot */}
                    <div
                      style={{ position: 'relative', cursor: 'pointer' }}
                      onClick={() => setInspectedFriend({ userId: friend.userId, name: friend.displayName })}
                      title={isMn ? `${friend.displayName} профайл үзэх` : `View ${friend.displayName}'s profile`}
                    >
                      <div
                        style={{
                          width: 42,
                          height: 42,
                          borderRadius: '50%',
                          background: friend.tier === 'paid'
                            ? 'linear-gradient(135deg, #f59e0b, #ef4444)'
                            : 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 700,
                          color: '#fff',
                          fontSize: '1rem',
                        }}
                      >
                        {friend.displayName.slice(0, 1).toUpperCase()}
                      </div>
                      <span
                        title={friend.online ? t.onlineStatus : t.offlineStatus}
                        style={{
                          position: 'absolute',
                          bottom: 0,
                          right: 0,
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          backgroundColor: friend.online ? '#22c55e' : '#64748b',
                          border: '2px solid #111827',
                          boxShadow: friend.online ? '0 0 6px #22c55e' : 'none',
                        }}
                      />
                    </div>

                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span
                          style={{
                            fontWeight: 600,
                            color: '#fff',
                            fontSize: '0.95rem',
                            cursor: 'pointer',
                            textDecoration: 'underline',
                            textDecorationColor: 'rgba(255, 255, 255, 0.25)',
                          }}
                          onClick={() => setInspectedFriend({ userId: friend.userId, name: friend.displayName })}
                          title={isMn ? `${friend.displayName} профайл үзэх` : `View ${friend.displayName}'s profile`}
                        >
                          {friend.displayName}
                        </span>
                        {friend.tier === 'paid' && (
                          <span
                            style={{
                              background: 'rgba(251, 191, 36, 0.2)',
                              color: '#fbbf24',
                              border: '1px solid rgba(251, 191, 36, 0.4)',
                              borderRadius: 4,
                              padding: '1px 5px',
                              fontSize: '0.7rem',
                              fontWeight: 700,
                            }}
                          >
                            VIP
                          </span>
                        )}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 2, fontSize: '0.8rem', color: 'rgba(255,255,255,0.6)' }}>
                        <span style={{ color: levelInfo.tierColor, fontWeight: 600 }}>
                          {levelInfo.rankBadge} {t.levelLabel(levelInfo.level)}
                        </span>
                        <span>·</span>
                        <span>{t.totalXpLabel(friend.xp)}</span>
                        <span>·</span>
                        <span style={{ color: friend.online ? '#4ade80' : 'rgba(255,255,255,0.4)' }}>
                          {friend.online ? t.onlineStatus : t.offlineStatus}
                        </span>
                      </div>
                      {friend.currentRoomId && (
                        <div style={{ marginTop: 4, fontSize: '0.75rem', color: '#93c5fd' }}>
                          {t.inRoomStatus(friend.currentRoomId)}
                        </div>
                      )}
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {friend.currentRoomId && (
                      <button
                        type="button"
                        onClick={() => {
                          if (friend.currentRoomId) {
                            actions.joinRoom(friend.currentRoomId, state.profile?.displayName || 'Player');
                            onJoinRoom?.(friend.currentRoomId);
                          }
                        }}
                        style={{
                          background: 'rgba(59, 130, 246, 0.2)',
                          color: '#60a5fa',
                          border: '1px solid rgba(59, 130, 246, 0.4)',
                          borderRadius: 6,
                          padding: '6px 12px',
                          fontSize: '0.85rem',
                          fontWeight: 600,
                          cursor: 'pointer',
                        }}
                      >
                        {isMn ? 'Өрөөнд орох' : 'Join Game'}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => handleRemoveFriend(friend.userId, friend.displayName)}
                      style={{
                        background: 'transparent',
                        color: 'rgba(255,255,255,0.4)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: 6,
                        padding: '6px 10px',
                        fontSize: '0.8rem',
                        cursor: 'pointer',
                      }}
                      title={t.removeFriend}
                    >
                      ✕
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Outgoing Requests */}
      {outgoing.length > 0 && (
        <div
          style={{
            background: 'rgba(255, 255, 255, 0.02)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            borderRadius: 12,
            padding: 16,
          }}
        >
          <h4 style={{ margin: '0 0 10px 0', fontSize: '0.88rem', color: 'rgba(255,255,255,0.7)' }}>
            {t.outgoingFriendRequests} ({outgoing.length})
          </h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {outgoing.map((req) => (
              <div
                key={req.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '6px 10px',
                  borderRadius: 6,
                  background: 'rgba(255,255,255,0.03)',
                  fontSize: '0.85rem',
                }}
              >
                <span style={{ color: 'rgba(255,255,255,0.8)' }}>
                  👤 {req.toDisplayName}
                </span>
                <span style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.4)' }}>
                  {isMn ? 'Хүлээгдэж буй...' : 'Pending...'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {inspectedFriend && (
        <PlayerProfileModal
          userId={inspectedFriend.userId}
          initialPlayerName={inspectedFriend.name}
          onClose={() => setInspectedFriend(null)}
        />
      )}
    </div>
  );
}
